<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AnHeader from "./AnHeader.vue";
import AnGauge from "./AnGauge.vue";
import AnRankBars from "./AnRankBars.vue";
import AnDonut from "./AnDonut.vue";
import AnHBars from "./AnHBars.vue";
import AnAreaLine from "./AnAreaLine.vue";
import AnalyticsService from "@/services/analytics";
import { useCompanyStore } from "@/stores/company";

const companyStore = useCompanyStore();
const COLORS = { good: "#34d399", bad: "#fb7185", accent: "#38bdf8", accent2: "#8b7cf6", warn: "#fbbf24" };

// ── state ────────────────────────────────────────────────────────
const period = ref("daily");
const dateRange = ref(null);
const loading = ref(false);

const gaugePct = ref(0);
const inStockCount = ref(0);
const outStockCount = ref(0);
const topInStock = ref([]);
const topOutStock = ref([]);
const typeSegments = ref([]);
const wayfinding = ref([]);
const trendSeries = ref([]);

// ── helpers ──────────────────────────────────────────────────────
function buildParams() {
  const p = { period: period.value };
  if (dateRange.value?.[0] && dateRange.value?.[1]) {
    p.from_date = dateRange.value[0].toISOString();
    p.until_date = dateRange.value[1].toISOString();
  }
  return p;
}

// ── fetch ────────────────────────────────────────────────────────
async function fetchData() {
  const companyId = companyStore.companyId;
  if (!companyId) return;
  loading.value = true;
  try {
    const params = buildParams();
    const [inRes, outRes, mapsRes, typeRes, stockRes, attachRes] = await Promise.all([
      AnalyticsService.getAttachmentsInStockCards(companyId, params),
      AnalyticsService.getAttachmentsOutStockCards(companyId, params),
      AnalyticsService.getAttachmentsMaps(companyId, params),
      AnalyticsService.getAttachmentsTypeDistribution(companyId, params),
      AnalyticsService.getAttachmentsStockStatus(companyId, params),
      AnalyticsService.getMessageAttachmentAnalytics(companyId, params),
    ]);

    // Gauge — from stock-status distribution
    if (stockRes?.distribution?.length) {
      const inD = stockRes.distribution.find((d) => d.stock_status === "in_stock");
      const outD = stockRes.distribution.find((d) => d.stock_status === "out_of_stock");
      gaugePct.value = (inD?.percentage ?? 0) / 100;
      inStockCount.value = inD?.count ?? 0;
      outStockCount.value = outD?.count ?? 0;
    }

    // Top in-stock products (items[] from cards endpoint)
    if (inRes?.items?.length) {
      topInStock.value = inRes.items.slice(0, 6).map((item) => ({
        name: item.product_title || item.name || "—",
        sub: item.brand || "",
        value: item.attachment_count ?? 0,
        color: COLORS.good,
      }));
    }

    // Top out-of-stock products
    if (outRes?.items?.length) {
      topOutStock.value = outRes.items.slice(0, 6).map((item) => ({
        name: item.product_title || item.name || "—",
        sub: item.brand || "",
        value: item.attachment_count ?? 0,
        color: COLORS.bad,
      }));
    }

    // Type distribution → AnDonut
    if (typeRes?.distribution?.length) {
      const palette = [COLORS.accent, COLORS.accent2, COLORS.warn, COLORS.good];
      typeSegments.value = typeRes.distribution.map((d, i) => ({
        label: d.attachment_type || d.type || "other",
        value: d.count ?? 0,
        color: palette[i % palette.length],
      }));
    }

    // Maps → AnHBars
    if (mapsRes?.items?.length) {
      wayfinding.value = mapsRes.items.slice(0, 8).map((m) => ({
        label: (m.title || "").replace(/\*\*/g, "").trim() || "Unknown",
        value: m.attachment_count ?? 0,
        gradient: true,
      }));
    }

    // Rich-content trend from attachment-analytics
    if (attachRes?.recent_timeline?.length) {
      const vals = attachRes.recent_timeline.map((d) => d.messages_with_attachments ?? 0);
      trendSeries.value = [{ values: vals, color: COLORS.warn, fill: "rgba(251,191,36,.14)" }];
    }
  } catch (e) {
    console.error("AttachmentsBoard fetch error:", e);
  } finally {
    loading.value = false;
  }
}

const centerTop = computed(() => (gaugePct.value * 100).toFixed(1) + "%");
const typeCenterTop = computed(() => {
  if (!typeSegments.value.length) return "—";
  const cards = typeSegments.value.find((t) => t.label === "cards");
  if (!cards) return "—";
  const total = typeSegments.value.reduce((s, t) => s + t.value, 0);
  return total ? ((cards.value / total) * 100).toFixed(1) + "%" : "—";
});

watch([period, dateRange], fetchData);
onMounted(fetchData);
</script>

<template>
  <div class="an-page">
    <AnHeader
      title="Stocks"
      subtitle="Catalog health, published product cards, and wayfinding demand by destination."
      @period-change="p => { period = p.toLowerCase(); }"
      @date-change="dr => { dateRange = dr; }"
    />

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="an-grid">
        <div class="an-panel an-c4"><div class="an-skel an-skel--chart"><Skeletor height="14" width="45%" as="div" /><Skeletor height="120" width="120" as="div" style="margin:0 auto" /><Skeletor height="10" width="40%" as="div" style="margin:0 auto" /></div></div>
        <div class="an-panel an-c4"><div class="an-skel"><Skeletor height="14" width="50%" as="div" /><Skeletor v-for="j in 5" :key="j" height="12" as="div" /></div></div>
        <div class="an-panel an-c4"><div class="an-skel"><Skeletor height="14" width="50%" as="div" /><Skeletor v-for="j in 5" :key="j" height="12" as="div" /></div></div>
        <div class="an-panel an-c3"><div class="an-skel an-skel--chart"><Skeletor height="14" width="45%" as="div" /><Skeletor height="100" width="100" as="div" style="border-radius:50%;margin:0 auto" /></div></div>
        <div class="an-panel an-c4"><div class="an-skel"><Skeletor height="14" width="50%" as="div" /><Skeletor v-for="j in 4" :key="j" height="12" as="div" /></div></div>
        <div class="an-panel an-c5"><div class="an-skel an-skel--chart"><Skeletor height="14" width="40%" as="div" /><Skeletor height="130" as="div" /></div></div>
      </div>
    </template>

    <div v-else class="an-grid">
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Catalog health</div></div>
        <div class="an-gauge">
          <AnGauge :pct="gaugePct" />
          <div class="an-gauge__num">{{ centerTop }}</div>
          <div class="an-gauge__cap">in stock</div>
          <div class="an-gauge__split">
            <div><b style="color:#34d399">{{ inStockCount.toLocaleString() }}</b> in stock</div>
            <div><b style="color:#fb7185">{{ outStockCount.toLocaleString() }}</b> out</div>
          </div>
        </div>
      </div>
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Published cards — in stock</div></div>
        <AnRankBars v-if="topInStock.length" :rows="topInStock" />
        <div v-else class="an-empty-panel">No data</div>
      </div>
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Restock priority — out of stock</div></div>
        <AnRankBars v-if="topOutStock.length" :rows="topOutStock" />
        <div v-else class="an-empty-panel">No out-of-stock data</div>
      </div>

      <div class="an-panel an-c3">
        <div class="an-panel__head"><div class="an-panel__title">Type split</div></div>
        <AnDonut v-if="typeSegments.length" :segments="typeSegments" :center-top="typeCenterTop" center-sub="published cards" />
        <div v-else class="an-empty-panel">No type data</div>
      </div>
      <div class="an-panel an-c4">
        <div class="an-panel__head"><div class="an-panel__title">Wayfinding demand</div></div>
        <AnHBars v-if="wayfinding.length" :rows="wayfinding" />
        <div v-else class="an-empty-panel">No maps data</div>
      </div>
      <div class="an-panel an-c5">
        <div class="an-panel__head"><div class="an-panel__title">Rich-content trend · 30 days</div></div>
        <AnAreaLine v-if="trendSeries.length" :series="trendSeries" :height="150" />
        <div v-else class="an-empty-panel">No trend data</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.an-empty-panel { color: var(--an-muted); font-size: 12px; padding: 16px 0; }
</style>
